import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import firebaseApp, { db } from 'services/firebase';

const LOCATIONS_COLLECTION = 'locations';
const VISITS_SUBCOLLECTION = 'visits';
const DEPARTMENTS_SUBCOLLECTION = 'departments';
const CLINIC_MESSAGES_SUBCOLLECTION = 'clinicMessages';

const CREATE_CHECKIN_FUNCTION = 'createCheckInVisit';
const SEND_BROADCAST_FUNCTION = 'sendClinicBroadcast';
const CREATE_DEPARTMENT_FUNCTION = 'createDepartment';
const UPDATE_DEPARTMENT_FUNCTION = 'updateDepartment';
const DELETE_DEPARTMENT_FUNCTION = 'deleteDepartment';

/**
 * Public check-in URL for a clinic. The clinic id (a Firestore location doc id)
 * is the only thing encoded — the link is meant to be displayed/shared, so it
 * carries no secret. An optional departmentId is appended as `?dept=` so a
 * department-specific QR tags the resulting check-in.
 */
export const buildCheckInUrl = (clinicId, departmentId) => {
  const base = `${window.location.origin}/checkin/${clinicId}`;
  return departmentId ? `${base}?dept=${encodeURIComponent(departmentId)}` : base;
};

/**
 * Patient self-check-in. The Cloud Function writes the visit with the admin SDK
 * (the visits subcollection is server-write-only) and always takes the patient
 * id from the auth context, so the client can't check anyone else in. An optional
 * departmentId tags the visit (validated server-side; ignored if it doesn't exist).
 */
export const submitCheckIn = async (clinicId, departmentId) => {
  if (!clinicId) throw new Error('Missing clinic.');
  const callable = firebaseApp.functions().httpsCallable(CREATE_CHECKIN_FUNCTION);
  try {
    const { data } = await callable({ clinicId, departmentId: departmentId || undefined });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not check you in. Please try again.');
  }
};

/**
 * Live list of a facility's departments. Readable only by clinic members per
 * Firestore rules.
 */
export const subscribeDepartments = (clinicId, onChange, onError) => {
  if (!clinicId) return () => {};
  return db
    .collection(LOCATIONS_COLLECTION)
    .doc(clinicId)
    .collection(DEPARTMENTS_SUBCOLLECTION)
    .orderBy('name', 'asc')
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * Create a department. Writes go through the Cloud Function (admin SDK,
 * membership-gated); the subcollection is server-write-only.
 */
export const createDepartment = async ({ clinicId, name, specialty }) => {
  if (!clinicId) throw new Error('Missing clinic.');
  const callable = firebaseApp.functions().httpsCallable(CREATE_DEPARTMENT_FUNCTION);
  try {
    const { data } = await callable({ clinicId, name, specialty: specialty || null });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not create the department. Please try again.');
  }
};

/**
 * Rename a department / change its specialty.
 */
export const updateDepartment = async ({ clinicId, departmentId, name, specialty }) => {
  if (!clinicId || !departmentId) throw new Error('Missing department.');
  const callable = firebaseApp.functions().httpsCallable(UPDATE_DEPARTMENT_FUNCTION);
  try {
    const { data } = await callable({ clinicId, departmentId, name, specialty });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not update the department. Please try again.');
  }
};

/**
 * Delete a department.
 */
export const deleteDepartment = async ({ clinicId, departmentId }) => {
  if (!clinicId || !departmentId) throw new Error('Missing department.');
  const callable = firebaseApp.functions().httpsCallable(DELETE_DEPARTMENT_FUNCTION);
  try {
    const { data } = await callable({ clinicId, departmentId });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not delete the department. Please try again.');
  }
};

/**
 * Live list of recent check-ins for a clinic (facility-side). Readable only by
 * clinic members per Firestore rules.
 */
export const subscribeRecentVisits = (clinicId, onChange, onError) => {
  if (!clinicId) return () => {};
  return db
    .collection(LOCATIONS_COLLECTION)
    .doc(clinicId)
    .collection(VISITS_SUBCOLLECTION)
    .orderBy('checkedInAt', 'desc')
    .limit(100)
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * Broadcast a message to everyone who checked in to the clinic recently. The
 * Cloud Function resolves recipients and fans out into each patient's
 * server-write-only clinicMessages subcollection.
 */
export const sendClinicBroadcast = async ({ clinicId, body, departmentId }) => {
  if (!clinicId) throw new Error('Missing clinic.');
  const callable = firebaseApp.functions().httpsCallable(SEND_BROADCAST_FUNCTION);
  try {
    const { data } = await callable({ clinicId, body, departmentId: departmentId || undefined });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not send the message. Please try again.');
  }
};

/**
 * Live list of clinic messages for the signed-in patient.
 */
export const subscribeMyClinicMessages = (uid, onChange, onError) => {
  if (!uid) return () => {};
  return db
    .collection('users')
    .doc(uid)
    .collection(CLINIC_MESSAGES_SUBCOLLECTION)
    .orderBy('createdAt', 'desc')
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * Mark one clinic message as read. Rules allow the owner to update only the
 * status/readAt fields on their own messages.
 */
export const markClinicMessageRead = async (uid, msgId) => {
  if (!uid || !msgId) return;
  await db
    .collection('users')
    .doc(uid)
    .collection(CLINIC_MESSAGES_SUBCOLLECTION)
    .doc(msgId)
    .update({
      status: 'read',
      readAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
};
